import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { colors } from '../../../../__styling/settings/colors'
import {
    breakpoint,
    breakpointMax,
} from '../../../../__styling/style-functions'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
import { themedValue } from '../../../../__styling/themed-value'
import { StyledButton } from '../../../../buttons/Button/Button.styled'
import {
    BreachWallContentType,
    SubscriptionPackageType,
} from '../../helpers/BreachWallTypes'
import {
    BreachOptions,
    PackageOptions,
    PackageThemeProps,
} from '../BreachWallContent/BreachWallContent'

const SubscribeWithGoogle = require('./SubscribeWithGoogle.svg')

export const StyledSubscriptionPackage = styled('div')<PackageThemeProps>(
    ({ theme, breachOptions }) => ({
        flexBasis: calcRem(278),
        flexShrink: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        padding: calcRem(theme.margins.md, 24),
        borderRadius: 6,
        fontFamily: theme.fonts.subscriber.text,
        background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(191, 205, 216, 0.5) 100%), #FFF;',
        color: themedValue(theme, {
            thewest: colors.thewest.westblue,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        boxShadow: breachOptions.isRegional
            ? '0 2px 21px 0 rgba(47, 48, 104, 0.4)'
            : '0 2px 21px 0 rgba(0,0,0,0.5)',
        [breakpointMax('xxs')]: {
            flexBasis: calcRem(250),
        },
    }),
)

export const StyledSubscriptionPackage2022 = styled(
    StyledSubscriptionPackage,
)<PackageThemeProps>(({ theme, packageOptions, breachOptions }) => [
    {
        flexBasis:
            packageOptions.theme === 'optimise-blue'
                ? calcRem(328)
                : calcRem(312),
        margin: 0,
        padding:
            breachOptions.breachType === '50-off-campaign'
                ? calcRem(16)
                : calcRem(24),
        background: theme.colors.background.primary || 'white',
        boxShadow: undefined,
        minHeight:
            breachOptions.breachType === '50-off-campaign'
                ? calcRem(676)
                : packageOptions.theme === 'optimise-blue'
                ? calcRem(466)
                : 'auto',

        '&:nth-child(2n)': {
            h4: {
                margin: undefined,
            },
        },

        [breakpointMax('md')]: {
            flexBasis: calcRem(312),
        },
        [breakpointMax('xs')]: {
            minHeight: 'auto',
            height: 'auto',
        },
        [breakpointMax('xxs')]: {
            flexGrow: 1,
        },
    },
    // PROMO: Vanishing Cousins
    breachOptions.breachType === 'vanishing-cousins' && {
        '&:nth-child(2n)': {
            button: {
                background: colors.white,
                color: colors.thewest.westblue,
                border: `2px solid ${colors.thewest.westblue}`,

                '&:focus': {
                    background: colors.thewest.greyKoala,
                    color: colors.white,
                    border: 'none',
                },

                '&:hover': {
                    background: colors.thewest.greyGorilla,
                    color: colors.white,
                    border: 'none',
                },
            },
        },
    },
    // PROMO: Black Friday
    breachOptions.breachType === 'black-friday' && {
        minHeight: calcRem(576),
    },
])

interface StyledPackageImageProps {
    isRegional?: boolean
    isEditionsModal?: boolean
    packageType?: SubscriptionPackageType
    breachType?: BreachWallContentType
    customScale?: number
    padding?: number
}

export const StyledPackageImage = styled('img')<StyledPackageImageProps>(
    ({
        isRegional,
        isEditionsModal,
        breachType,
        packageType,
        customScale,
        padding = 0,
    }) => ({
        width: isEditionsModal ? '75%' : '95%',
        display: 'block',
        margin: '0 auto',
        marginTop:
            isRegional && !isEditionsModal
                ? calcRem(25)
                : (breachType === 'black-friday' ||
                      breachType === '50-off-campaign') &&
                  packageType === 'digital'
                ? calcRem(6.4)
                : 0,
        transform: customScale ? `scale(${customScale})` : 'scale(1.1)',
        height: '180px',
        objectFit: 'contain',
        padding: calcRem(padding),
    }),
)

export const StyledMiniPackageImage = styled(StyledPackageImage)({
    height: 'auto',
    transform: 'unset',
})

export const StyledSubscriptionPackageHeading = styled('h4')<PackageThemeProps>(
    ({ theme }) => ({
        fontSize: calcRem(24),
        fontWeight: 700,
        lineHeight: 1.167,
        margin: calcRem(theme.margins.md, 0, theme.margins.sm, 0),
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),

        flex: '0 0 auto', // fixes ie11 column wrap bug
    }),
)

export const StyledSubscriptionPackageHeading2022 = styled(
    'h4',
)<PackageThemeProps>(({ theme, breachOptions, packageOptions }) => [
    {
        fontFamily: theme.fonts.serif,
        fontSize: calcRem(22),
        lineHeight: 1,
        fontWeight: undefined,
        margin:
            packageOptions.theme === 'optimise-blue'
                ? calcRem(theme.margins.md, 0, theme.margins.xs, 0)
                : calcRem(theme.margins.md, 0, theme.margins.sm, 0),
        marginBottom:
            breachOptions.breachType === '50-off-campaign'
                ? calcRem(16)
                : undefined,

        color: themedValue(theme, {
            thewest: colors.black,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),

        flex: '0 0 auto', // fixes ie11 column wrap bug
    },
    breachOptions.breachType === 'black-friday' && {
        minHeight: calcRem(44),
    },
])

export const StyledSubscriptionPackagePriceDivider2022 = styled(
    'hr',
)<PackageThemeProps>(({ breachOptions }) => ({
    width: '100%',
    maxWidth:
        breachOptions.breachType === 'black-friday'
            ? calcRem(112)
            : calcRem(53),
    height: calcRem(4),
    backgroundColor:
        breachOptions.isRegional && breachOptions.useBreachDealStyle
            ? tokens.thewest.colors.palette.westblue
            : tokens.thewest.colors.palette.westblue,
    marginLeft: 0,
    borderRadius: calcRem(4),
    border: 'none',
    marginTop: calcRem(6),
    marginBottom: calcRem(16),
}))

export const StyledSubscriptionPackageActions = styled('div')<{
    paddingBot?: number
    isLast?: boolean
    isSwgPage?: boolean
}>(({ paddingBot, isLast, isSwgPage }) => [
    {
        width: '100%',
        alignSelf: 'center',
        textAlign: 'center',
        lineHeight: 'normal',
        flexGrow: isLast ? 1 : 0,
        alignContent: isLast ? 'end' : 'normal',
    },
    paddingBot && {
        padding: calcRem(0, 0, paddingBot ?? 0),
    },
    isSwgPage && {
        padding: calcRem(20, 0),
    },
])

export const StyledSubscriptionPackageButton = styled(
    StyledButton, //target styledbutton
)<PackageThemeProps>(({ theme, packageOptions, breachOptions }) => ({
    margin: calcRem(theme.margins.md, 0, 0),
    width: calcRem(200),
    height: calcRem(40),
    fontSize: calcRem(16),

    [breakpoint('xxs')]: {
        fontSize: calcRem(18),
        width: calcRem(230),
    },

    backgroundColor:
        packageOptions.theme === 'red'
            ? colors.thewest.ladybird
            : packageOptions.theme === 'yellow'
            ? colors.thewest.sunshineYellow
            : breachOptions.isRegional && breachOptions.useBreachDealStyle
            ? tokens.thewest.colors.palette.westblue
            : packageOptions.theme === 'west-blue'
            ? colors.thewest.westblue
            : undefined,

    background:
        packageOptions.theme === 'red'
            ? colors.thewest.ladybird
            : packageOptions.theme === 'yellow'
            ? colors.thewest.sunshineYellow
            : breachOptions.isRegional && breachOptions.useBreachDealStyle
            ? tokens.thewest.colors.palette.westblue
            : packageOptions.theme === 'west-blue'
            ? colors.thewest.westblue
            : undefined,
}))

export const StyledSubscriptionPackageButton2022 = styled(
    StyledSubscriptionPackageButton,
)<PackageThemeProps>({
    fontWeight: 500,
    width: '100%',
    textTransform: 'none',
    transition: '0s background ease',
    [breakpoint('xxs')]: { width: '100%' },
})

export const StyledSubscriptionCancellationMessage = styled(
    'p',
)<PackageThemeProps>(({ theme, packageOptions, breachOptions }) => [
    {
        fontSize: calcRem(12),
        flex: '0 0 auto', // fixes ie11 column wrap bug
        fontWeight: 400,
        lineHeight: 1.167,
        margin: 'auto 0 0',
        color: themedValue(theme, {
            thewest: colors.thewest.greyRhino,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),
        paddingTop:
            breachOptions.breachType === '50-off-campaign'
                ? calcRem(24)
                : calcRem(theme.margins.sm),
    },
])

export const Ribbon = styled('p')<PackageThemeProps>(
    ({ theme, packageOptions, breachOptions }) => ({
        zIndex: 1,
        position: 'absolute',
        top: 0,
        right: 0,
        background:
            packageOptions.theme === 'red'
                ? colors.thewest.ladybird
                : packageOptions.theme === 'yellow'
                ? colors.thewest.sunshineYellow
                : packageOptions.theme === 'west-blue' ||
                  breachOptions.breachType === '50-off-campaign'
                ? colors.thewest.westblue
                : breachOptions.breachType === 'black-friday'
                ? tokens.thewest.colors.palette.darkYellow
                : breachOptions.breachType === 'summer-of-cricket'
                ? '#f5db0b'
                : 'linear-gradient(45deg, #aa01e1 0%,#f400ab 100%)',
        transform:
            packageOptions.theme === 'vanishing-cousins'
                ? 'rotate(45deg) translate(11%, -105%)'
                : breachOptions.breachType === 'black-friday'
                ? 'rotate(45deg) translate(15%, -107%)'
                : 'rotate(45deg) translate(15%,-100%)',
        height: 80,
        width: 160,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        textTransform: 'uppercase',
        fontSize:
            packageOptions.theme === 'vanishing-cousins' ||
            breachOptions.breachType === 'black-friday'
                ? calcRem(32)
                : calcRem(10),
        fontWeight: 700,
        boxShadow:
            breachOptions.breachType === 'black-friday'
                ? '0 1px 3px 0 rgba(0,0,0,0.1)'
                : '0 1px 3px 0 rgba(0,0,0,0.5)',

        span: {
            color:
                breachOptions.breachType === 'black-friday'
                    ? tokens.thewest.colors.palette.black
                    : breachOptions.breachType === 'summer-of-cricket'
                    ? '#095938'
                    : colors.white,
            display: 'block',
            width: 50,
            textAlign: 'center',
            lineHeight: 1,
            marginBottom:
                packageOptions.theme === 'vanishing-cousins' ||
                breachOptions.breachType === 'black-friday'
                    ? 0
                    : theme.margins.xs,
        },

        svg: {
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, calc(-100% - 5px))',
        },

        '&.cornerDesignTag': {
            height: 94,
            width: 234,
            fontSize:
                packageOptions.theme === 'vanishing-cousins'
                    ? calcRem(32)
                    : breachOptions.breachType === 'black-friday'
                    ? calcRem(20)
                    : 24,
            flexDirection: 'column',
            alignItems: 'center',
            lineHeight: '91%',

            span: {
                width:
                    packageOptions.theme === 'vanishing-cousins' ||
                    breachOptions.breachType === 'black-friday'
                        ? '100%'
                        : 50,

                '&:first-child': {
                    paddingTop: calcRem(20),
                    fontSize:
                        packageOptions.theme === 'vanishing-cousins' ||
                        breachOptions.breachType === 'black-friday'
                            ? calcRem(20)
                            : 'inherit',
                    marginTop:
                        packageOptions.theme === 'vanishing-cousins' ||
                        breachOptions.breachType === 'black-friday'
                            ? 'auto'
                            : undefined,
                    marginBottom:
                        packageOptions.theme === 'vanishing-cousins' ||
                        breachOptions.breachType === 'black-friday'
                            ? undefined
                            : 'inherit',
                    lineHeight:
                        packageOptions.theme === 'vanishing-cousins' ||
                        breachOptions.breachType === 'black-friday'
                            ? 0.9
                            : 'inherit',
                },

                '&:last-child': {
                    marginBottom:
                        packageOptions.theme === 'vanishing-cousins'
                            ? calcRem(7.2)
                            : breachOptions.breachType === 'black-friday'
                            ? calcRem(12)
                            : 0,
                },
            },
            svg: {
                display: 'none',
            },
        },

        '&.newDesignTag': {
            position: 'unset',
            transform: 'none',
            height: 'auto',
            width: '90px',
            padding: '10px',
            paddingRight: '25px',
            marginTop: '20px',
            marginBottom: '-5px',
            borderRadius: '30px',

            span: {
                position: 'relative',
                margin: 0,
                fontSize: '0.65rem',

                svg: {
                    left: 'unset',
                    right: '-16px',
                    width: '12px',
                    transform: 'translateY(-3px)',
                },
            },
        },

        '&.bluePill': {
            transform: 'none',
            height: 'auto',
            width: '80px',
            padding: calcRem(8, 4),
            marginTop: calcRem(10),
            marginRight: calcRem(18),
            borderRadius: '30px',
            background: colors.thewest.westblue,

            span: {
                margin: 0,
                fontSize: calcRem(12),
                width: 'auto',
            },
        },
    }),
)

export const StyledPromoTagline = styled('p')<{ hasAsset: boolean }>(
    ({ hasAsset }) => ({
        fontSize: calcRem(16),
        fontWeight: 600,
        color: tokens.thewest.colors.palette.black,
        paddingLeft: hasAsset ? '20px' : undefined,
        marginTop: 0,
        position: 'relative',

        img: {
            position: 'absolute',
            left: '0px',
            top: calcRem(4),
            transform: 'rotate(-15deg)',
            width: '16px',
            height: '16px',
            objectFit: 'cover',
        },
    }),
)

export const StyledSubscriptionPackageBenefit = styled.div<{
    packageOptions?: PackageOptions
    breachOptions?: BreachOptions
}>(({ theme, packageOptions, breachOptions }) => ({
    fontFamily: theme.fonts.serif,
    fontSize:
        packageOptions?.theme === 'optimise-blue'
            ? calcRem(14)
            : breachOptions?.breachType === 'black-friday'
            ? calcRem(14)
            : calcRem(16),
    color: 'black',
    display: 'flex',
    margin: calcRem(6, 0),
    alignItems: 'center',
    textAlign: 'start',

    '& > :first-child': {
        flex: '0 0 ' + calcRem(20),
    },
    '& > :last-child': {
        flex: '1 1 auto',
        marginLeft: calcRem(8),
    },
}))

export const StyledSubscribeWithGoogleButton = styled('button')(
    ({ theme }) => ({
        marginTop: theme.margins.sm,
        cursor: 'pointer',
        maxWidth: '100%',
        borderRadius: '4px',
    }),
)

export const StyledSubscribeWithGoogleButton2022 = styled(
    StyledSubscribeWithGoogleButton,
)({
    width: '100%',
    padding: 0,
    borderRadius: calcRem(2),
    border: `1px solid ${colors.thewest.greyKoala}`,
    boxShadow: 'none',
    '&::after': {
        content: "''",
        position: 'relative',
        display: 'inline-block',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url('${SubscribeWithGoogle}')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
    },
    '&:hover': {
        backgroundColor: '#f8f8f0',
    },
    '&.regionalsswgpromogooglebutton': {
        marginTop: '0',
    },
})

// News Breach V2 2024
export const StyledSubscriptionButton2024 = styled(
    StyledSubscriptionPackageButton2022,
)<PackageThemeProps>(({ buttonStyle, breachOptions }) => [
    {
        background:
            'linear-gradient(270deg, rgb(220, 1, 190) 0%, rgb(170, 1, 225) 100%)',
        fontSize: '20px',
        height: '2.2em',
    },
    buttonStyle === 'solid-blue' && {
        background: colors.thewest.westblue,
    },
    buttonStyle === 'solid-red' && {
        background: '#9E1D22',
    },
    buttonStyle === 'transparent' && {
        background: 'transparent',
        color: colors.thewest.westblue,
        border: `1px solid ${colors.thewest.westblue}`,
    },
])

export const StyledSwgButton2024 = styled(StyledSubscribeWithGoogleButton)(
    () => ({
        width: '100%',
        padding: 0,
        border: 'none',
        borderRadius: calcRem(2),
        '&::after': {
            content: "''",
            position: 'relative',
            display: 'inline-block',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url('${SubscribeWithGoogle}')`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
        },
        '&:hover': {
            backgroundColor: '#f8f8f0',
        },
    }),
)
